
import React, { useState } from 'react';
import { RevenueHeader } from '@/components/revenue/RevenueHeader';
import { RevenueFilters, PaymentType, DateRange } from '@/components/revenue/RevenueFilters';
import { RevenueStatsCards } from '@/components/revenue/RevenueStatsCards';
import { PaymentsTable } from '@/components/revenue/PaymentsTable';
import { useStudents } from '@/contexts';
import { useTeacherData } from '@/hooks/useTeacherData';
import { useRevenueCalculations } from '@/hooks/useRevenueCalculations';
import { printRevenueStatement, printRevenueBreakdown, printTeacherSalaryReport } from '@/utils/printing/revenue';

const RevenuePage = () => {
  const { students, loading: studentsLoading } = useStudents();
  const { teachers, loading: teachersLoading } = useTeacherData();
  const [paymentType, setPaymentType] = useState<PaymentType>('all');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [customDate, setCustomDate] = useState<Date>();

  const { stats, filteredPayments } = useRevenueCalculations(
    students,
    teachers,
    paymentType,
    dateRange,
    searchQuery,
    customDate
  );

  const handlePrintStatement = async (format: 'jpg' | 'pdf') => {
    console.log('Print statement with format:', format);
    await printRevenueStatement(stats, format);
  };

  const handlePrintBreakdown = async (format: 'jpg' | 'pdf') => {
    console.log('Print breakdown with format:', format);
    const revenueData = students.flatMap(student =>
      student.programs.map(program => ({
        student: student.name,
        program: program.title,
        amount: program.fee || 0,
        status: program.feeStatus || 'pending',
        enrollmentDate: program.enrolledAt ? program.enrolledAt.toString() : '',
        nextPaymentDate: program.nextPaymentDate ? program.nextPaymentDate.toString() : ''
      }))
    );
    await printRevenueBreakdown(revenueData, format);
  };
  
  const handlePrintTeacherSalaries = async (format: 'jpg' | 'pdf') => {
    console.log('Print teacher salaries with format:', format);
    console.log('Teachers data:', teachers);
    
    // Create salary data from teachers with better error handling
    const salaryData = teachers.flatMap(teacher => {
      console.log('Processing teacher:', teacher.firstName, teacher.lastName, 'payments:', teacher.payments);
      
      if (teacher.payments && teacher.payments.length > 0) {
        return teacher.payments.map(payment => ({
          teacherId: teacher.id || 'N/A',
          teacherName: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || 'N/A',
          department: teacher.department || (teacher.departments && teacher.departments[0]?.name) || 'General',
          paymentDate: payment.paymentDate || new Date().toISOString(),
          paymentPeriod: payment.paymentPeriod || 'Monthly',
          amount: payment.amount || 0,
          status: payment.status || 'pending'
        }));
      } else if (teacher.salary && parseFloat(teacher.salary) > 0) {
        // If no payment records but has salary, create a default entry
        return [{
          teacherId: teacher.id || 'N/A',
          teacherName: `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || 'N/A',
          department: teacher.department || (teacher.departments && teacher.departments[0]?.name) || 'General',
          paymentDate: new Date().toISOString(),
          paymentPeriod: 'Monthly',
          amount: parseFloat(teacher.salary) || 0,
          status: teacher.paymentStatus || 'pending'
        }];
      }
      return [];
    });
    
    console.log('Final salary data:', salaryData);
    await printTeacherSalaryReport(salaryData, format);
  };

  const programCount = students.reduce((count, student) => count + student.programs.length, 0);
  const teacherPaymentsCount = teachers.filter(t => t.payments && t.payments.length > 0).length;

  return (
    <div className="space-y-6 p-6">
      <RevenueHeader
        onPrintStatement={handlePrintStatement}
        onPrintBreakdown={handlePrintBreakdown}
        onPrintTeacherSalaries={handlePrintTeacherSalaries}
        filteredPayments={filteredPayments}
      />

      <RevenueFilters
        paymentType={paymentType}
        dateRange={dateRange}
        searchQuery={searchQuery}
        customDate={customDate}
        onPaymentTypeChange={setPaymentType}
        onDateRangeChange={setDateRange}
        onSearchQueryChange={setSearchQuery}
        onCustomDateChange={setCustomDate}
      />

      <RevenueStatsCards
        stats={stats}
        programCount={programCount}
        teacherPaymentsCount={teacherPaymentsCount}
      />

      <PaymentsTable
        payments={filteredPayments}
        loading={studentsLoading || teachersLoading}
      />
    </div>
  );
};

export default RevenuePage;
